<script setup lang="ts">
import { Sh } from '@/core/sharedService';
import { FormDesignItemDto, GridExcelDto, RowGridExcelDto, DisplayType, PropertyDisplayType } from '@/core/store/survey/surveyDesignDtos';
import { SurveyResponseItemDto } from '@/core/store/survey/surveyResponseDtos';
import Survey from '@/core/store/survey/surveyStore';
import { computed, CSSProperties, ref, watch } from 'vue';

const {
  displayMode,
  isDesign,
  state,
  getNewTag,
  findTag,
  validTag,
  calculateFormulas,
  
} = Survey();


const props = defineProps({
  item: {
    type: Object as () => FormDesignItemDto,
    required: true
  },
  labelStyle: {
    type: Object as () => CSSProperties,
    required: true
  },
  contentStyle: {
    type: Object as () => CSSProperties,
    required: true
  },
  response: {
    type: Object as () => SurveyResponseItemDto,
    default: null,
    required: false
  }
});

// Computed properties para la grilla
const gridData = computed(() => {
  if (!props.item.gridDesign) {
    props.item.gridDesign = new GridExcelDto();
  }
  
  return props.item.gridDesign;
});
const value = ref<SurveyResponseItemDto>(props.response && props.response.value ? props.response.value : []);
// Computed property para los datos de respuesta
const responseData = computed(() => {
  if (!props.response?.value) {
    // Si no hay datos de respuesta, creamos un array vacío con la estructura correcta
    const rows = gridData.value.rows.length || 0;
    const cols = gridData.value.headers.length || 0;
    return Array(rows).fill(null).map(() => 
      Array(cols).fill(0)
    );
  }
  
  // Asegurarnos de que la respuesta tenga la estructura correcta
  const responseValue = props.response;
  const rows = gridData.value.rows.length;
  const cols = gridData.value.headers.length;
  
  // Si faltan filas, las añadimos
  while (responseValue.value.length < rows) {
    responseValue.value.push(Array(cols).fill(0));
  }
  
  // Nos aseguramos de que cada fila tenga el número correcto de columnas
  for (let i = 0; i < responseValue.value.length; i++) {
    if (!responseValue.value[i]) {
      responseValue.value[i] = Array(cols).fill(0);
    } else {
      while (responseValue.value[i].length < cols) {
        responseValue.value[i].push(0);
      }
    }
  }
  
  return responseValue.value;
});
  const isReference = computed<boolean>(() => {
    return !Sh.isNullOrEmptyOrZero(props.item.inheritItemId)
  })

const columnHeaders = computed(() => {
  return gridData.value.headers;
});

const rowHeaders = computed(() => {
  return gridData.value.rows.map(row => row.title);
});

// Computed property para el número total de columnas
const totalColumns = computed(() => {
  return columnHeaders.value.length;
});

// Método para generar array de índices de columnas
const columnIndices = computed(() => {
  return Array.from({ length: totalColumns.value }, (_, i) => i);
});

// Métodos para manejar la grilla
const updateCell = (rowIndex: number, colIndex: number, value: string) => {
  if (!gridData.value.rows[rowIndex]) {
    gridData.value.rows[rowIndex] = new RowGridExcelDto();
  }
  // Eliminamos espacios al inicio y final antes de guardar
  gridData.value.rows[rowIndex].cells[colIndex].tag = value.trim();
};

const getDefaultTag = (rowIndex: number, colIndex: number) => {
  const tag = {tag: getNewTag(), isReference: false};
  return tag;
}

const validateCellTagValue = (value: string, rowIndex: number, colIndex: number): boolean => {
  return validTag(value, props.item.id+"."+rowIndex+"."+colIndex);
};

const initializeCell = (rowIndex: number, colIndex: number) => {
  if (!gridData.value.rows[rowIndex]) {
    gridData.value.rows[rowIndex] = new RowGridExcelDto();
  }
  if (!gridData.value.rows[rowIndex].cells[colIndex].tag) {
    gridData.value.rows[rowIndex].cells[colIndex] = getDefaultTag(rowIndex, colIndex);
  }
};

const getCellValue = (rowIndex: number, colIndex: number) => {
  initializeCell(rowIndex, colIndex);
  // Aseguramos que siempre retornamos el valor sin espacios
  return gridData.value.rows[rowIndex].cells[colIndex].tag.trim();
};

let previousValue: string | null = null;

const onCellFocus = (rowIndex: number, colIndex: number) => {
  // Guardamos el valor actual antes de que el usuario lo edite
  previousValue = gridData.value.rows[rowIndex].cells[colIndex].tag;
};

const onCellBlur = (rowIndex: number, colIndex: number) => {
  const curRow = gridData.value.rows[rowIndex];
  const curCell = curRow.cells[colIndex];
  let currentValue = curCell.tag.trim();
  
  // Comparamos los valores sin espacios al inicio y final
  if (currentValue.trim() === previousValue?.trim()) {
    // Si solo cambió espacios, guardamos el valor sin espacios
    updateCell(rowIndex, colIndex, currentValue.trim());
    return;
  }
  
  if (!currentValue || currentValue.trim() === '') {
    // Si está vacío, mantenemos el valor que ya tenía
    updateCell(rowIndex, colIndex, previousValue);
    return;
  }
  if(curCell.isReference && !isReference.value) {
    const tagParts = currentValue.split(".");
    const curSingleTag = tagParts[0];
    if(tagParts.length>1 && tagParts[0] + ".value" !== currentValue) {
      curCell.tag = tagParts[0] + ".value";    
      currentValue = curCell.tag;
    }
    const itemTagId = findTag(curSingleTag);
    if(!itemTagId || itemTagId == colIndex.toString().trim() + "-" + curRow.title) {
        // Si no es un tag válido, restauramos el valor anterior
        updateCell(rowIndex, colIndex, previousValue );
        return;
    }
  }else if (!validateCellTagValue(currentValue.trim(), rowIndex, colIndex)) {
    // Si no es válido, restauramos el valor anterior
    updateCell(rowIndex, colIndex, previousValue);
    return;
  }

  // Si llegamos aquí, el valor es válido y diferente al anterior
  previousValue = currentValue.trim();
};

const addRow = () => {
  if (isDesign.value) {
    const newRow = new RowGridExcelDto();
    newRow.title = `Fila ${gridData.value.rows.length + 1}`;
    // Inicializamos las celdas de la nueva fila
    newRow.cells = Array(totalColumns.value).fill('').map((_, i) => getDefaultTag(gridData.value.rows.length, i));
    gridData.value.rows.push(newRow);
  }
};

const addColumn = () => {
  if (isDesign.value) {
    const newColIndex = gridData.value.headers.length;
    gridData.value.headers.push(`Columna ${newColIndex + 1}`);
    // Inicializamos las celdas de la nueva columna
    gridData.value.rows.forEach((row, rowIndex) => {
      row.cells.push(getDefaultTag(rowIndex, newColIndex));
    });
  }
};

const removeRow = (index: number) => {
  if (isDesign.value) {
    gridData.value.rows.splice(index, 1);
  }
};

const removeColumn = (index: number) => {
  if (isDesign.value) {
    gridData.value.headers.splice(index, 1);
    gridData.value.rows.forEach(row => {
      row.cells.splice(index, 1);
    });
  }
};

// Métodos para manejar las cabeceras
const updateColumnHeader = (index: number, value: string) => {
  if (isDesign.value) {
    gridData.value.headers[index] = value;
  }
};

const updateRowHeader = (index: number, value: string) => {
  if (isDesign.value) {
    gridData.value.rows[index].title = value;
  }
};
/* const editBuffer = ref(responseData.value.map(row => [...row]));
watch(props.response, (newVal) => {
  editBuffer.value = newVal.value.map(row => [...row]);
});
const onCellBlurUpdate = (rowIndex: number, colIndex: number) => {
  updateResponseData(rowIndex, colIndex, editBuffer.value[rowIndex][colIndex]);
}; */
// Método para actualizar los datos de respuesta
const updateResponseData = (rowIndex: number, colIndex: number, value: string) => {
  if (!props.response) return;
  
  // Convertimos el valor a número, permitiendo decimales
  const numValue = parseFloat(value) || 0;
  
  // Aseguramos que el array existe
  if (!props.response.value) {
    props.response.value = Array(gridData.value.rows.length).fill(null).map(() => 
      Array(gridData.value.headers.length).fill(0)
    );
  }
  
  // Actualizamos el valor
  props.response.value[rowIndex][colIndex] = numValue;
  calculateFormulas()
};

const toggleIsRef = (rowIndex: number, colIndex: number) => {
    const cell = gridData.value.rows[rowIndex].cells[colIndex];
    if(cell.isReference === undefined || cell.isReference === null || cell.isReference === false) {
      cell.isReference = true;
      cell.tag  = "";
    }else{
      cell.isReference = false;
      cell.tag = getNewTag();
    }
};
</script>

<template>
  <div class="excel-grid-container">
    <div class="excel-grid-wrapper">
      <div class="excel-grid">
        <!-- Encabezados de columnas -->
        <div class="grid-header-row">
          <div class="grid-corner"></div>
          <div v-for="(header, index) in columnHeaders" 
               :key="'col-' + index" 
               class="grid-header-cell">
            <input v-if="isDesign && displayMode === DisplayType.EDIT"
                   type="text"
                   :value="header"
                   :style="props.labelStyle"
                   @input="(e: Event) => updateColumnHeader(index, (e.target as HTMLInputElement).value)"
                   class="header-input" />
            <span v-else   :style="props.labelStyle">
              {{ header  }}
            </span>
            <img v-if="isDesign && displayMode === DisplayType.EDIT && !isReference" 
                    class="btn-icon delete-btn"
                    src="/assets/objectButtonsIcons/delcol.svg"
                    style="height: 16px; width: 30px;"
                    @click="removeColumn(index)"
                    title="Eliminar columna" />
          </div>
        </div>
        
        <!-- Filas de datos -->
        <div v-for="(row, rowIndex) in gridData.rows" 
             :key="'row-' + rowIndex" 
             class="grid-row">

             <!-- Row headers -->
          <div class="grid-row-header">
            <input v-if="isDesign && displayMode === DisplayType.EDIT"
                   type="text"
                   :style="props.labelStyle"
                   :value="row.title"
                   @input="(e: Event) => updateRowHeader(rowIndex, (e.target as HTMLInputElement).value)"
                   class="header-input" />
            <span  v-else :style="props.labelStyle">{{ row.title  }}</span>
            <img v-if="isDesign && displayMode === DisplayType.EDIT && !isReference" 
                    class="btn-icon delete-btn"
                    @click="removeRow(rowIndex)"
                    title="Eliminar fila"
                    src="/assets/objectButtonsIcons/delrow.svg" />
          </div>
          

          <!-- row cells -->
          <div v-for="(tag, colIndex) in row.cells" 
               :key="'cell-' + rowIndex + '-' + colIndex" 
               class="grid-cell">

               <!-- Modo diseño -->
            <div v-if="isDesign" class="cell-preview" :style="{...props.contentStyle, position: 'relative', backgroundColor: (gridData.rows[rowIndex].cells[colIndex].isReference ? '#D3EDE6': 'transparent')}">
                <input v-if="displayMode === DisplayType.EDIT"
                       type="text"
                       :style="{...props.contentStyle, textAlign: props.contentStyle.textAlign || 'center', backgroundColor: (gridData.rows[rowIndex].cells[colIndex].isReference ? '#D3EDE6': 'transparent'), width: item.sufix.trim().length ? '85%' : '100%'}"
                       v-model="gridData.rows[rowIndex].cells[colIndex].tag"
                       @focus="() => onCellFocus(rowIndex, colIndex)"
                       @blur="() => onCellBlur(rowIndex, colIndex)"
                       class="cell-input" />

                 <img v-if="displayMode === DisplayType.EDIT && !isReference" class="link-ref" :src="gridData.rows[rowIndex].cells[colIndex].isReference
                 ?  '/assets/link.svg' : '/assets/link.png'" @click="() => toggleIsRef(rowIndex,colIndex)"/> 
                
                <div v-if="displayMode !== DisplayType.EDIT" :style="{...props.contentStyle, display: 'inline-block', textAlign: props.contentStyle.textAlign || 'center', backgroundColor: (gridData.rows[rowIndex].cells[colIndex].isReference ? '#D3EDE6': 'transparent'), width: item.sufix.trim().length ? '85%' : '100%'}">{{ gridData.rows[rowIndex].cells[colIndex].tag }}</div>
                <span v-if="item.sufix.trim().length" style="display: inline;"> {{item.sufix}}</span>                       
            </div
            >
            <!-- modo edicion -->

            <div v-else class="cell-preview" :style="{...props.contentStyle, backgroundColor: (gridData.rows[rowIndex].cells[colIndex].isReference ? '#99c1b6': '#D3EDE6')}">
                <input v-if="displayMode === DisplayType.EDIT"
                       type="text"
                       inputmode="decimal"
                       :disabled="gridData.rows[rowIndex].cells[colIndex].isReference || isReference"
                       pattern="[0-9]*[.,]?[0-9]*"
                       :style="gridData.rows[rowIndex].cells[colIndex].isReference ? {...props.contentStyle, textAlign: props.contentStyle.textAlign || 'right', backgroundColor: '#99c1b6 !important', width: item.sufix.trim().length ? '85%' : '100%'} : {...props.contentStyle, textAlign: props.contentStyle.textAlign || 'right', width: item.sufix.trim().length ? '85%' : '100%'}"
                      v-model="props.response.value[rowIndex][colIndex]"
                      @ended="(e) => console.log('end', e.target)"
                       @focusout="(e) => console.log('out', e.target)"
                       @blur="(e) => updateResponseData(rowIndex, colIndex, (e.target as HTMLInputElement).value)"
                       class="cell-input numeric-input" />
                       <!-- "(e: Event) => updateResponseData(rowIndex, colIndex, (e.target as HTMLInputElement).value)" -->
                       <!-- @input="(e: Event) => updateResponseData(rowIndex, colIndex, (e.target as HTMLInputElement).value)" -->
                
                <div v-else :style="{...props.contentStyle, textAlign: props.contentStyle.textAlign || 'center', backgroundColor: (gridData.rows[rowIndex].cells[colIndex].isReference ? '#D3EDE6': 'transparent'), width: item.sufix.trim().length ? '85%' : '100%', display: 'inline-block'}" class="numeric-value">{{ responseData[rowIndex]?.[colIndex] ?? 0 }}</div>
                <span v-if="item.sufix.trim().length" style="display: inline;"> {{item.sufix}}</span>                       
            </div>

          </div>
        </div>
      </div>
    </div>

    <div class="grid-controls" v-if="isDesign && displayMode === DisplayType.EDIT && !isReference">
      <button class="btn-primary" @click="addRow">
        <img src="/assets/objectButtonsIcons/addrow.svg" style="width: 24px;" alt="Añadir Fila" />
        Añadir Fila
      </button>
      <button class="btn-primary" @click="addColumn">
        <img src="/assets/objectButtonsIcons/addcol.svg" style="height: 16px;" alt="Añadir Columna" />
        Añadir Columna
      </button>
    </div>
    <div class="grid-controls" v-if="isDesign && displayMode === DisplayType.EDIT && isReference">
      <div style="font-size: small;">(Referenciado) </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.excel-grid-container {
  width: 100%;
  background: white;
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.excel-grid-wrapper {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #D3EDE6;
  border-radius: 4px;
}

.excel-grid {
  width: max-content;
  min-width: 100%;
}

.grid-header-row {
  display: flex;
  background-color: #f8f9fa;
  border-bottom: 1px solid #D3EDE6;
}

.grid-corner {
  width: 120px;
  min-width: 120px;
  flex-shrink: 0;
  border-right: 1px solid #D3EDE6;
  background-color: #f8f9fa;
  position: sticky;
  left: 0;
  z-index: 2;
}

.grid-header-cell {
  flex: 1;
  min-width: 120px;
  padding: 0.75rem;
  border-right: 1px solid #D3EDE6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  color: #126192;
}

.grid-row {
  display: flex;
  border-bottom: 1px solid #D3EDE6;

  &:last-child {
    border-bottom: none;
  }
}

.grid-row-header {
  width: 120px;
  min-width: 120px;
  flex-shrink: 0;
  padding: 0.75rem;
  border-right: 1px solid #D3EDE6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  position: sticky;
  left: 0;
  z-index: 1;
  color: #126192;
}

.grid-cell {
  flex: 1;
  min-width: 120px;
  padding: 0.5rem;
  border-right: 1px solid #D3EDE6;
  display: flex;
  align-items: center;
  justify-content: center;

  &:last-child {
    border-right: none;
  }
}

.header-input {
  flex: 1;
  border: none;
  background: transparent;
  color: inherit;
  font-size: 0.875rem;
  padding: 0.25rem;
  
  &:hover, &:focus {
    background: #f5f5f5;
    outline: none;
    border-radius: 4px;
  }
}

.cell-preview {
  width: 100%;
  background-color: #D3EDE6;
  padding: 0.5rem;
  border-radius: 4px;
  text-align: center;
  color: #126192;

  input {
    &[value^="Tag"] {
      color: rgba(18, 97, 146, 0.5);
    }

    &:focus {
      color: #126192;
    }
  }
}

.grid-controls {
  display: flex;
  gap: 1rem;
  padding: 0.5rem 0;
}

.btn-primary {
  background-color: #126192;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    background-color: darken(#126192, 10%);
  }
}

.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #f0f0f0;
  color: #666;
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  
  &:hover {
    background-color: #ff4444;
    color: white;
  }
}

::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #D3EDE6;
  border-radius: 4px;
  
  &:hover {
    background: darken(#D3EDE6, 10%);
  }
}

.cell-input {
  width: 100%;
  border: none;
  background: transparent;
  color: #126192;
  font-size: 0.875rem;
  
  &:hover, &:focus {
    background: #f5f5f5;
    outline: none;
    border-radius: 4px;
    color: #126192;
  }

  &.numeric-input {
    padding-right: 0.5rem;
  }
}

.numeric-value {
  text-align: right;
  padding-right: 0.5rem;
  display: block;
  width: 100%;
}

.link-ref{
  width: 15px;
  position: absolute;
  top: 1px;
  left: 1px;
}
</style> 